import React, {useCallback, useState} from 'react';
import dayjs from 'dayjs';
import {t} from '../../../../localization';
import {DatePicker, Layout} from '@di-nxt/components';

type Props = {
  fromDate: string | undefined;
  toDate: string | undefined;
  onFromDateChanged: (from: Date) => void;
  onToDateChanged: (to: Date) => void;
};

export const PeriodEditor: React.FC<Props> = ({
  fromDate: initialFromDate,
  toDate: initialToDate,
  onFromDateChanged,
  onToDateChanged,
}) => {
  // State
  const [fromDate, setFromDate] = useState<Date | undefined>(
    initialFromDate !== undefined
      ? dayjs(initialFromDate).hour(0).minute(0).toDate()
      : undefined,
  );
  const [toDate, setToDate] = useState<Date | undefined>(
    initialToDate !== undefined
      ? dayjs(initialToDate).hour(23).minute(59).toDate()
      : undefined,
  );

  // Handlers
  const handleFromDateChanged = useCallback(
    (date: Date) => {
      let resolved = dayjs(date);
      if (toDate !== undefined) {
        resolved = dayjs.min(dayjs(resolved), dayjs(toDate));
      }
      resolved = resolved.hour(0);
      resolved = resolved.minute(0);
      setFromDate(resolved.toDate());
      onFromDateChanged(resolved.toDate());
    },
    [onFromDateChanged, toDate],
  );

  const handleToDateChanged = useCallback(
    (date: Date) => {
      let resolved = dayjs(date);
      if (fromDate !== undefined) {
        resolved = dayjs.max(dayjs(resolved), dayjs(fromDate));
      }
      resolved = resolved.hour(23);
      resolved = resolved.minute(59);
      setToDate(resolved.toDate());
      onToDateChanged(resolved.toDate());
    },
    [fromDate, onToDateChanged],
  );

  // Render
  return (
    <Layout
      horizontal
      align="center"
      height={64}
      backgroundColor="background_mono_tertiary"
      borderRadius="4">
      <DatePicker
        flex
        selectedDate={fromDate}
        onDateChanged={handleFromDateChanged}
        title={t('screens.schedule.absencedialog.from.caption')}
        unselectedTitle={t('screens.schedule.absencedialog.from.unselected')}
      />
      <Layout width={1} height={32} backgroundColor="border_mono_15" />
      <DatePicker
        flex
        selectedDate={toDate}
        title={t('screens.schedule.absencedialog.to.caption')}
        onDateChanged={handleToDateChanged}
        unselectedTitle={t('screens.schedule.absencedialog.to.unselected')}
      />
    </Layout>
  );
};
